import { Badge, Button, Drawer, Popover, TooltipTextOverflow } from '@systemeio/components'
import { FilterLinesIcon, LayoutRightIcon, RefreshCw01Icon, XCloseIcon } from '@systemeio/icons'
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useFilterSidebar } from 'shared/components/filter-sidebar'
import { FormControlSizeProvider } from 'shared/components/form-control-size-context'
import { ViewportSwitch } from 'shared/components/viewport-switch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useMediaQuery from 'shared/hooks/use-media-query'
import { twJoin, twMerge } from 'tailwind-merge'

/** Field width where a section sets no `--filter-field-width`. Mobile never reaches it. */
const FIELD_WIDTH = { desktop: 280, tablet: 260 }

/**
 * Gap between the measured parts, in px. Inline rather than a `gap-*` class so the value that
 * draws the space is the same one the fits/does-not-fit math subtracts; a consumer's `className`
 * must not override it. Rows the math does not measure keep the ordinary `gap-3`.
 */
const GAP = 12

export interface HeaderFilterControl {
  key: React.Key
  node: ReactNode
}

export interface HeaderFilterGroup {
  controls: HeaderFilterControl[]
  activeCount: number
  dirty: boolean
  canApply: boolean
  clear: () => void
  apply: () => boolean
  setInline: (inline: boolean) => void
  discard: () => void
  /**
   * Always collapsed: a side drawer on desktop/tablet, a bottom drawer on mobile. On a desktop page
   * with a `FilterSidebarLayout` the panel docks into its column instead and stays open.
   */
  complex?: boolean
  /**
   * Everything below the panel's own title, composed out of `FilterPanel.*` — the only way
   * section-specific content reaches the popover or drawer. Left out, the header renders the
   * controls and, in a drawer, a footer with Clear all and Apply.
   */
  panel?: ReactNode
}

type FilterPanelShell = 'popover' | 'side-drawer' | 'bottom-drawer' | 'sidebar'

interface FilterPanelContextValue {
  shell: FilterPanelShell
  /** Left out while the panel is closed: only the open container may hold the controls host. */
  slotRef?: (slot: HTMLDivElement | null) => void
  clear: ReactNode
  apply: ReactNode
}

const FilterPanelContext = createContext<FilterPanelContextValue | undefined>(undefined)

function useFilterPanel(part: string) {
  const context = useContext(FilterPanelContext)
  if (!context)
    throw new Error(`FilterPanel.${part} is only valid inside a filter group's \`panel\`.`)
  return context
}

/*
 * Walks the ancestors instead of calling `checkVisibility()`, which Safari gained only in 17.4 and
 * jsdom does not have.
 */
const isDisplayNone = (element: HTMLElement | null) => {
  for (let node = element; node; node = node.parentElement)
    if (getComputedStyle(node).display === 'none') return true
  return false
}

export interface FilterHeaderProps {
  leadingContent?: ReactNode
  title?: ReactNode
  counter?: ReactNode
  help?: ReactNode
  filters?: HeaderFilterGroup
  /**
   * One entry per visible Action — a dropdown with several items is one entry, a falsy entry is an
   * Action the page hides. §2 forbids inferring them from the JSX, and their number decides
   * placement, so the array is the classification.
   */
  actions?: ReactNode[]
  className?: string
}

export function FilterHeader({
  title,
  leadingContent,
  counter,
  help,
  filters,
  actions,
  className,
}: FilterHeaderProps) {
  const { t } = useLocoTranslation()
  const visibleActions = (actions ?? []).filter(Boolean)
  const actionCount = visibleActions.length
  const { isMobile, isDesktop } = useMediaQuery()
  const sidebar = useFilterSidebar()
  const docked = !!sidebar && isDesktop && !!filters?.complex
  const [open, setOpen] = useState(false)
  const [host, setHost] = useState<HTMLDivElement | null>(null)
  const [widths, setWidths] = useState({
    container: null as number | null,
    title: 0,
    actions: 0,
    clear: 0,
    mobile: 0,
    field: 0,
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const mobileActionsRef = useRef<HTMLDivElement>(null)
  const desktopActionsRef = useRef<HTMLDivElement>(null)
  const actionsRef = isMobile ? mobileActionsRef : desktopActionsRef
  const clearRef = useRef<HTMLButtonElement>(null)
  const controlsRef = useRef<HTMLDivElement>(null)
  const movingFocus = useRef<HTMLElement | null>(null)
  const focusedControl = useRef<HTMLElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const fixedActions = isMobile && actionCount >= 2
  const headerActions = fixedActions ? 0 : actionCount
  const count = filters?.controls.length ?? 0
  // A complex group is an editor behind one control, never one logical filter, so it keeps the
  // numeric badge and Clear all instead of the single-filter dot.
  const singleFilter = count === 1 && !filters?.complex
  const singleMobileFilter = isMobile && singleFilter
  const showBadge = !!filters?.activeCount && !singleMobileFilter
  const layoutPending = widths.container === null && !filters?.complex
  const containerWidth = widths.container ?? 0
  const fieldWidth = widths.field || (isDesktop ? FIELD_WIDTH.desktop : FIELD_WIDTH.tablet)
  const rightAligned =
    !isMobile &&
    !filters?.complex &&
    count === 1 &&
    actionCount === 0 &&
    fieldWidth + widths.title + GAP <= containerWidth
  const groupWidth =
    count * fieldWidth + Math.max(0, count - 1) * GAP + (count > 1 ? widths.clear + GAP : 0)
  const collapsed =
    !!filters &&
    (filters.complex ||
      (isMobile
        ? // A lone control owns the whole second row, so it always fits and only an Action can
          // collapse it; from two controls up their natural widths decide.
          headerActions > 0 || (count > 1 && widths.mobile > containerWidth)
        : groupWidth > containerWidth))
  const secondRow =
    isMobile ||
    (!collapsed &&
      // Actions add a second gap to the row: Title · group · Actions, against Title · group alone.
      groupWidth + widths.title + widths.actions + (actionCount ? GAP * 2 : GAP) > containerWidth)
  const layout = collapsed ? 'collapsed' : secondRow ? 'row' : 'inline'
  const panelOpen = collapsed && open && !docked
  // Where the controls live in a panel: an opened overlay, or the docked column that never closes.
  const panelShown = panelOpen || docked
  const measuring = isMobile && collapsed && !panelOpen && !filters?.complex
  const stackedControls = collapsed && !measuring
  /*
   * Inline — and in the second row — the group shares the header's own line, so its fields are the
   * compact 32px control. An open overlay (popover, side drawer, bottom drawer) is a surface of its
   * own with the room for the ordinary 40px field, and the panel's own Clear / Apply follow the
   * fields beside them. The off-screen copy stays compact deliberately: it measures the width the
   * group would need if it went back inline, which is a 32px row. The docked column is not an
   * overlay: it stays on the page beside the content and keeps the compact size (§3).
   */
  const controlSize = panelOpen ? 'md' : 'sm'
  const drawer = isMobile || filters?.complex
  const Trigger = drawer ? Drawer.Trigger : Popover.Trigger
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isMobile) filters?.discard()
    setOpen(nextOpen)
  }

  useLayoutEffect(() => {
    if (!layoutPending) filters?.setInline(!collapsed)
  }, [collapsed, layoutPending, filters?.setInline])

  // A drawer left open on tablet would reopen by itself after a trip through the docked desktop.
  useLayoutEffect(() => {
    if (docked) setOpen(false)
  }, [docked])

  useLayoutEffect(() => {
    setHost(document.createElement('div'))
  }, [])
  useLayoutEffect(() => {
    const measure = () => {
      // A hidden ancestor (a tab panel kept mounted) lays the header out at zero width, which
      // would collapse the group and flash the Filter button on the next reveal.
      if (isDisplayNone(containerRef.current)) return
      setWidths(previous => {
        const next = {
          field: containerRef.current
            ? Number.parseFloat(
                getComputedStyle(containerRef.current).getPropertyValue('--filter-field-width'),
              ) || 0
            : 0,
          container: containerRef.current?.clientWidth ?? 0,
          title: titleRef.current?.scrollWidth ?? 0,
          actions: fixedActions ? 0 : (actionsRef.current?.scrollWidth ?? 0),
          clear: clearRef.current?.getBoundingClientRect().width ?? 0,
          // The row's own overflow, never the sum of its children: inline the controls stretch to
          // fill the row, so their rounded widths exceed the container and collapse a group that
          // fits, while the off-screen copy reports the natural width and expands it again.
          mobile:
            isMobile && !panelOpen && controlsRef.current
              ? controlsRef.current.scrollWidth
              : previous.mobile,
        }
        return Object.keys(next).every(
          key => next[key as keyof typeof next] === previous[key as keyof typeof next],
        )
          ? previous
          : next
      })
    }
    measure()
    const observer = new ResizeObserver(measure)
    for (const ref of [containerRef, titleRef, actionsRef, clearRef, controlsRef])
      if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [
    fixedActions,
    title,
    actions,
    count,
    host,
    isMobile,
    isDesktop,
    panelOpen,
    measuring,
    className,
  ])

  useLayoutEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!fixedActions || !parent) return
    const previous = parent.style.paddingBottom
    const update = () => {
      // The guard only keeps a stale zero from removing the space the bar still occupies.
      const height = actionsRef.current?.offsetHeight
      if (height) parent.style.paddingBottom = `${height}px`
    }
    update()
    const observer = new ResizeObserver(update)
    if (actionsRef.current) observer.observe(actionsRef.current)
    return () => {
      observer.disconnect()
      parent.style.paddingBottom = previous
    }
  }, [fixedActions])

  // Move one portal host between slots; controls and their local input state stay mounted.
  const attach = useCallback(
    (slot: HTMLDivElement | null) => {
      if (!host) return
      if (!slot) {
        movingFocus.current = host.contains(document.activeElement)
          ? (document.activeElement as HTMLElement)
          : null
        return
      }
      // A second `FilterPanel.Controls` steals the host and leaves the first slot empty. The
      // losing slot is still in the document, which is what tells this apart from an ordinary move.
      if (
        process.env.NODE_ENV !== 'production' &&
        host.parentElement !== slot &&
        host.parentElement?.isConnected
      )
        console.warn('FilterHeader: a panel renders the controls slot more than once.')
      slot.appendChild(host)
      if (movingFocus.current) {
        if (slot.inert) triggerRef.current?.focus({ preventScroll: true })
        else movingFocus.current.focus({ preventScroll: true })
        movingFocus.current = null
      }
    },
    [host],
  )

  // The last focused control may be gone (a removed condition), and a panel tab showing other
  // content keeps the whole host detached; neither can take the focus.
  const initialFocus = () =>
    (focusedControl.current?.isConnected && focusedControl.current) ||
    (controlsRef.current?.isConnected &&
      controlsRef.current.querySelector<HTMLElement>('input, button')) ||
    false
  const restoreFocus = () => triggerRef.current ?? focusedControl.current ?? false

  const clearButton = filters && !singleFilter && (!isMobile || collapsed) && (
    <Button
      type="button"
      variant={collapsed ? 'ghost-gray' : 'ghost-color'}
      size={panelOpen ? 'md' : 'sm'}
      onClick={filters.clear}
      disabled={!filters.dirty}
      className={twJoin(
        'shrink-0 whitespace-nowrap',
        collapsed && !drawer && 'self-end',
        !isMobile && !collapsed && !filters.dirty && 'invisible',
      )}
      tabIndex={filters.dirty ? undefined : -1}
      aria-hidden={!isMobile && !collapsed && !filters.dirty}
    >
      <RefreshCw01Icon aria-hidden />
      {t('dashboard.contacts.filter.clear_all')}
    </Button>
  )
  const applyButton = isMobile && collapsed && filters && (
    <Button
      type="button"
      variant="primary"
      size={panelOpen ? 'md' : 'sm'}
      disabled={!filters.canApply}
      className="shrink-0"
      onClick={() => {
        // Accepting the draft closes the panel; only dismissal discards it.
        if (filters.apply()) setOpen(false)
      }}
    >
      {t('global.apply')}
    </Button>
  )
  const mobileTriggerSize = showBadge ? 'md' : 'icon-md'
  // The docked panel never closes and carries its own Clear all, so the header needs no control
  // to open it or to report what is applied.
  const trigger = !docked && (
    <div
      className={twJoin('isolate inline-flex shrink-0 items-center', layoutPending && 'invisible')}
    >
      <Trigger
        ref={triggerRef}
        render={
          <Button
            type="button"
            variant="secondary"
            size={isMobile ? mobileTriggerSize : 'sm'}
            className={twJoin(
              'gap-1 focus-visible:z-10',
              !!filters?.activeCount && 'rounded-r-none',
            )}
          />
        }
        aria-label={t('global.filter')}
      >
        <span className="relative inline-flex">
          <FilterLinesIcon />
          {singleMobileFilter && !!filters?.activeCount && (
            <span
              aria-hidden
              className="bg-bg-accent-active ring-bg-primary absolute -top-0.5 -right-0.5 size-1.5 rounded-full ring-2"
            />
          )}
        </span>
        {!isMobile && <span>{t('global.filter')}</span>}
        {showBadge && (
          <Badge size="sm" shape="rounded" color="blue">
            {filters?.activeCount}
          </Badge>
        )}
      </Trigger>
      {!!filters?.activeCount && (
        <Button
          type="button"
          variant="secondary"
          size={isMobile ? 'icon-md' : 'icon-sm'}
          className="-ml-px rounded-l-none focus-visible:z-10"
          aria-label={t('dashboard.contacts.filter.clear_all')}
          onClick={filters.clear}
        >
          <XCloseIcon />
        </Button>
      )}
    </div>
  )
  const closeButton = (icon: ReactNode) => (
    <Button
      type="button"
      variant="ghost-gray"
      size="icon-sm"
      aria-label={t('global.close')}
      onClick={() => handleOpenChange(false)}
    >
      {icon}
    </Button>
  )
  const renderedActions = visibleActions.map((action, index) => (
    <Fragment key={index}>{action}</Fragment>
  ))
  const getShell = (): FilterPanelShell => {
    if (docked) return 'sidebar'
    if (!drawer) return 'popover'
    return isMobile ? 'bottom-drawer' : 'side-drawer'
  }
  const shell = getShell()
  const panelBody = (
    <FilterPanelContext.Provider
      value={{
        shell,
        slotRef: panelShown ? attach : undefined,
        clear: clearButton,
        apply: applyButton,
      }}
    >
      {filters?.panel ?? (
        <>
          <FilterPanel.Content>
            <FilterPanel.Controls />
          </FilterPanel.Content>
          {/* The popover keeps Clear all at the end of the control row instead (§6). */}
          {drawer && (clearButton || applyButton) && (
            <FilterPanel.Footer>
              {clearButton}
              {applyButton}
            </FilterPanel.Footer>
          )}
        </>
      )}
    </FilterPanelContext.Provider>
  )

  return (
    <Popover.Root open={panelOpen && !drawer} onOpenChange={handleOpenChange}>
      <Drawer.Root
        open={panelOpen && drawer}
        onOpenChange={handleOpenChange}
        side={isMobile ? 'bottom' : 'right'}
      >
        <div
          ref={containerRef}
          className={twMerge('relative flex min-h-8 min-w-0 flex-wrap items-center', className)}
          style={{ gap: GAP }}
          data-filter-layout={layout}
        >
          {/*
           * Only off the inline row may the title shrink, and its zero basis there keeps a long one
           * from wrapping the Actions to a row of their own. The placement reads its `scrollWidth`:
           * squeezed on the inline row, it would hold a group inline that no longer fits beside it.
           */}
          <div
            ref={titleRef}
            className={twJoin(
              'tablet:max-w-max flex min-w-0 flex-1 items-center gap-1 [&_[data-hint-trigger]_svg]:size-6',
              layout === 'inline' && 'tablet:flex-none',
            )}
          >
            {leadingContent}
            {title && (
              <div className="text-text-primary min-w-0 text-2xl font-semibold whitespace-nowrap">
                {/* An element title truncates itself: breadcrumbs carry a tooltip per item, and a
                    second one around them would repeat the whole trail with its links. */}
                {typeof title === 'string' || typeof title === 'number' ? (
                  <TooltipTextOverflow>{title}</TooltipTextOverflow>
                ) : (
                  title
                )}
              </div>
            )}
            {counter ? <div className="shrink-0">{counter}</div> : null}
            {help && <div className="shrink-0">{help}</div>}
          </div>
          {count > 1 && !isMobile && (
            <Button
              ref={clearRef}
              type="button"
              variant="ghost-color"
              size="sm"
              tabIndex={-1}
              aria-hidden
              className="pointer-events-none invisible absolute whitespace-nowrap"
            >
              <RefreshCw01Icon aria-hidden />
              {t('dashboard.contacts.filter.clear_all')}
            </Button>
          )}
          {collapsed && actionCount !== 1 && <>{trigger}</>}
          {filters && !collapsed && (
            <div
              ref={attach}
              className={twJoin(
                'min-h-8 min-w-0',
                layoutPending && 'invisible',
                secondRow ? 'order-3 w-full' : rightAligned ? 'ml-auto' : '',
              )}
            />
          )}
          {actionCount > 0 && (
            <ViewportSwitch
              at="tablet"
              below={
                <div
                  className={twJoin(
                    actionCount === 1
                      ? 'order-3 flex w-full min-w-0 items-center gap-3'
                      : 'contents',
                  )}
                >
                  {collapsed && actionCount === 1 && trigger}
                  <div
                    ref={mobileActionsRef}
                    className={twJoin(
                      'flex min-w-0 items-center gap-3 [&>*]:h-auto [&>*]:min-h-10 [&>*]:min-w-0 [&>*]:flex-1 [&>*]:whitespace-normal [&>*_button]:min-h-10',
                      actionCount === 1
                        ? 'flex-1'
                        : 'bg-bg-primary border-border-100 fixed inset-x-0 bottom-0 z-40 flex-wrap border-t px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] [&>*]:basis-auto',
                    )}
                  >
                    {renderedActions}
                  </div>
                </div>
              }
              from={
                <>
                  {collapsed && actionCount === 1 && trigger}
                  <div ref={desktopActionsRef} className="ml-auto flex shrink-0 items-center gap-3">
                    {renderedActions}
                  </div>
                </>
              }
            />
          )}
        </div>
        {collapsed && !panelShown && (
          <div
            ref={attach}
            aria-hidden
            inert
            className="pointer-events-none invisible fixed top-0 -left-[10000px] w-max"
          />
        )}
        {!drawer && (
          <Popover.Portal>
            <Popover.Positioner anchor={triggerRef} align="start" side="bottom">
              <Popover.Popup
                initialFocus={initialFocus}
                finalFocus={restoreFocus}
                className="w-90 max-w-[min(360px,var(--available-width))] p-4"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <Popover.Title>{t('global.filter')}</Popover.Title>
                  {closeButton(<XCloseIcon />)}
                </div>
                {panelBody}
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        )}
        {docked && sidebar?.element && createPortal(panelBody, sidebar.element)}
        {drawer &&
          !docked &&
          (isMobile ? (
            <Drawer.Popup initialFocus={initialFocus} finalFocus={restoreFocus}>
              <Drawer.Header className="flex-row items-center justify-between gap-3">
                <Drawer.Title>{t('global.filter')}</Drawer.Title>
                {singleMobileFilter && filters && (
                  <Button
                    type="button"
                    variant="ghost-color"
                    size="sm"
                    onClick={() => {
                      filters.clear()
                      handleOpenChange(false)
                    }}
                  >
                    {t('global.reset')}
                  </Button>
                )}
              </Drawer.Header>
              {panelBody}
            </Drawer.Popup>
          ) : (
            <Drawer.Popup
              initialFocus={initialFocus}
              finalFocus={restoreFocus}
              className="bg-bg-primary w-full max-w-105"
            >
              <div className="border-border-100 flex shrink-0 items-center justify-between gap-2.5 border-b px-6 py-4">
                <Drawer.Title className="text-text-primary text-xl font-semibold">
                  {t('global.filter')}
                </Drawer.Title>
                {closeButton(<LayoutRightIcon />)}
              </div>
              {panelBody}
            </Drawer.Popup>
          ))}
        {host &&
          filters &&
          createPortal(
            <FormControlSizeProvider size={controlSize}>
              <div
                ref={controlsRef}
                onFocusCapture={event => {
                  focusedControl.current = event.target as HTMLElement
                }}
                className={twJoin('flex', stackedControls ? 'flex-col' : 'items-center')}
                style={{ gap: GAP }}
                onKeyDown={event => {
                  const input = event.target
                  if (
                    event.key !== 'Enter' ||
                    event.defaultPrevented ||
                    event.nativeEvent.isComposing ||
                    !(input instanceof HTMLInputElement) ||
                    input.getAttribute('role') === 'combobox' ||
                    !['text', 'search', 'email', 'number', 'tel', 'url'].includes(input.type)
                  )
                    return
                  event.preventDefault()
                  if (!isMobile || !collapsed) filters.apply()
                }}
              >
                {filters.controls.map(control => (
                  <div
                    key={control.key}
                    style={
                      !collapsed && !isMobile
                        ? { width: fieldWidth }
                        : isMobile && !stackedControls && count > 1
                          ? { minWidth: 'max-content' }
                          : undefined
                    }
                    className={twJoin(
                      'min-w-0 [&>*]:w-full [&>*]:max-w-full [&>*]:min-w-0',
                      stackedControls ? 'w-full' : isMobile ? 'flex-1' : 'shrink-0',
                    )}
                  >
                    {control.node}
                  </div>
                ))}
                {!drawer && !(collapsed && filters?.panel) && clearButton}
              </div>
            </FormControlSizeProvider>,
            host,
          )}
      </Drawer.Root>
    </Popover.Root>
  )
}

/**
 * The chrome each container gives the panel parts. The bottom drawer's scrollable area is
 * `Drawer.Content` itself, so it takes no class of its own.
 */
const shellChrome: Record<FilterPanelShell, { section: string; content: string; footer: string }> =
  {
    popover: { section: '', content: 'max-h-[min(70dvh,var(--available-height))]', footer: 'pt-3' },
    'side-drawer': {
      section: 'px-6',
      content: 'min-h-0 flex-1 px-6 py-4',
      footer: 'border-border-100 border-t px-6 py-4',
    },
    'bottom-drawer': { section: 'px-4', content: '', footer: 'gap-3 [&>*]:min-w-0 [&>*]:flex-1' },
    sidebar: {
      section: 'px-5',
      content: 'min-h-0 flex-1 px-6 pt-3 pb-4',
      footer: 'border-border-200 border-t px-8 py-4',
    },
  }

/** A band that keeps the container's horizontal padding and does not scroll with the content. */
function FilterPanelSection({ children, className }: { children?: ReactNode; className?: string }) {
  const { shell } = useFilterPanel('Section')
  return (
    <div className={twMerge('shrink-0', shellChrome[shell].section, className)}>{children}</div>
  )
}

/** The scrollable area; insert FilterPanel.Controls explicitly to show the controls. */
function FilterPanelContent({ children }: { children?: ReactNode }) {
  const { shell } = useFilterPanel('Content')
  if (shell === 'bottom-drawer') return <Drawer.Content>{children}</Drawer.Content>
  return <div className={twJoin('overflow-y-auto', shellChrome[shell].content)}>{children}</div>
}

/** The slot the single portal host moves into. One panel renders it at most once. */
function FilterPanelControls() {
  const { slotRef } = useFilterPanel('Controls')
  return <div ref={slotRef} />
}

/** The footer of the panel, outside its scrollable area. */
function FilterPanelFooter({ children }: { children?: ReactNode }) {
  const { shell } = useFilterPanel('Footer')
  if (shell === 'bottom-drawer')
    return <Drawer.Footer className={shellChrome[shell].footer}>{children}</Drawer.Footer>
  return (
    <div
      className={twMerge(
        'flex shrink-0 items-center justify-between gap-3',
        shellChrome[shell].footer,
      )}
    >
      {children}
    </div>
  )
}

/** Renders nothing where §6 gives the group no Clear all. */
function FilterPanelClear() {
  const { clear } = useFilterPanel('Clear')
  return <>{clear}</>
}

/** Renders nothing outside a collapsed mobile group. */
function FilterPanelApply() {
  const { apply } = useFilterPanel('Apply')
  return <>{apply}</>
}

/**
 * The parts a group's `panel` is composed of. Each one carries the chrome of the container it
 * lands in, so a section orders its panel without knowing which container opened.
 */
export const FilterPanel = {
  Section: FilterPanelSection,
  Content: FilterPanelContent,
  Controls: FilterPanelControls,
  Footer: FilterPanelFooter,
  Clear: FilterPanelClear,
  Apply: FilterPanelApply,
}
